// Brigada de emergencia (PE-21, Kevin 26/10/2026). Igual que el CHECK de
// emergency_brigade_members en M33 (supabase/migrations/20261026070000_plan_emergencias_v2_modelo.sql).
// Una persona puede tener varios roles (una fila por rol). Jefe de brigada y coordinador de emergencia
// son cargos aparte. EmergencyPlanPage.tsx:24 ofrece hoy 4 roles; se cambia en PE-A3.

export const ROLES_BRIGADA = [
  { clave: 'evacuacion', etiqueta: 'Evacuación' },
  { clave: 'primeros_auxilios', etiqueta: 'Primeros auxilios' },
  { clave: 'contra_incendios', etiqueta: 'Lucha contra incendios' },
  { clave: 'materiales_peligrosos', etiqueta: 'Materiales peligrosos' },
  { clave: 'comunicacion', etiqueta: 'Comunicación' },
  { clave: 'otra', etiqueta: 'Otra (especificar)' },
] as const;

export const CARGOS_BRIGADA = [
  { clave: 'jefe_brigada', etiqueta: 'Jefe de brigada' },
  { clave: 'coordinador_emergencia', etiqueta: 'Jefe / coordinador de emergencia' },
] as const;

export type RolBrigada = typeof ROLES_BRIGADA[number]['clave'];
export type CargoBrigada = typeof CARGOS_BRIGADA[number]['clave'];

export interface MiembroBrigada {
  roles: RolBrigada[];
  otroRolTexto?: string; // obligatorio si roles incluye 'otra'
  cargo?: CargoBrigada | null;
}

// Devuelve los errores del miembro (lista vacía si está bien).
export function validarMiembroBrigada(m: MiembroBrigada): string[] {
  const errores: string[] = [];
  const validos = ROLES_BRIGADA.map(r => r.clave as string);
  if (m.roles.length === 0) errores.push('Elige al menos un rol');
  for (const r of m.roles) if (!validos.includes(r)) errores.push(`Rol desconocido: ${r}`);
  if (new Set(m.roles).size !== m.roles.length) errores.push('Un rol está repetido');
  if (m.roles.includes('otra') && !(m.otroRolTexto && m.otroRolTexto.trim())) errores.push('Escribe cuál es el otro rol');
  if (m.cargo && !CARGOS_BRIGADA.some(c => c.clave === m.cargo)) errores.push(`Cargo desconocido: ${m.cargo}`);
  return errores;
}

// ── PE-38 (Kevin): brigada capacitada ───────────────────────────────────────
// Cuenta como capacitada si CADA brigadista tiene una capacitación registrada en los últimos 12 meses. Fuente:
// emergency_brigade_members.trained y training_date (la pantalla de la brigada exige la fecha al marcar
// "capacitado", EmergencyPlanPage.tsx:228). El registro de capacitaciones (sso_trainings) no distingue las de
// brigada, así que no se usa. Una persona con varios roles cuenta una vez, con su fecha más reciente.
export interface FilaBrigadaCapacitacion { workerId: string; trained: boolean; trainingDate: string | null }
export type EstadoCapacitacion = 'capacitada' | 'no_capacitada' | 'sin_fecha' | 'sin_brigada';

export function capacitacionBrigada(filas: FilaBrigadaCapacitacion[], hoy: Date = new Date()): {
  estado: EstadoCapacitacion; personas: number; vigentes: number; sinFecha: number; vencidasONo: number;
} {
  const porPersona = new Map<string, FilaBrigadaCapacitacion[]>();
  for (const f of filas) porPersona.set(f.workerId, [...(porPersona.get(f.workerId) ?? []), f]);
  const limite = new Date(hoy); limite.setFullYear(limite.getFullYear() - 1);
  const limiteIso = `${limite.getFullYear()}-${String(limite.getMonth() + 1).padStart(2, '0')}-${String(limite.getDate()).padStart(2, '0')}`;
  let vigentes = 0, sinFecha = 0, vencidasONo = 0;
  for (const fs of Array.from(porPersona.values())) {
    const fechas = fs.filter(f => f.trained && f.trainingDate).map(f => f.trainingDate as string).sort();
    const ultima = fechas[fechas.length - 1];
    if (ultima && ultima >= limiteIso) vigentes++;
    else if (!ultima && fs.some(f => f.trained)) sinFecha++;
    else vencidasONo++;
  }
  const personas = porPersona.size;
  const estado: EstadoCapacitacion = personas === 0 ? 'sin_brigada' : vencidasONo > 0 ? 'no_capacitada' : sinFecha > 0 ? 'sin_fecha' : 'capacitada';
  return { estado, personas, vigentes, sinFecha, vencidasONo };
}
